import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  TrendingUp,
  TrendingDown,
  Users,
  Heart,
  MessageCircle,
  Share2,
  Eye,
  Calendar as CalendarIcon,
  RefreshCw,
  BarChart3,
  Instagram,
  Facebook,
  Linkedin,
  Video
} from 'lucide-react';
import { format, subDays } from 'date-fns';
import { useSocialAnalytics } from '@/hooks/social/useSocialAnalytics';
import { SocialAccount, SocialPlatform, PLATFORM_INFO, AnalyticsSummary } from '@/types/social';
import { cn } from '@/lib/core/utils';
import { FollowerGrowthChart } from './FollowerGrowthChart';
import { EngagementChart } from './EngagementChart';
import { PostPerformanceTable } from './PostPerformanceTable';
import { PlatformComparison } from './PlatformComparison';

interface SocialAnalyticsDashboardProps {
  businessId: string;
  accounts: SocialAccount[];
}

type DateRangePreset = '7d' | '14d' | '30d' | '90d' | 'custom';

export const SocialAnalyticsDashboard: React.FC<SocialAnalyticsDashboardProps> = ({
  businessId,
  accounts
}) => {
  const [datePreset, setDatePreset] = useState<DateRangePreset>('30d');
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const {
    summary,
    timeSeries,
    topPosts,
    isLoading,
    error,
    dateRange,
    setDateRange,
    selectedPlatforms,
    setSelectedPlatforms,
    refresh
  } = useSocialAnalytics(businessId);

  const hasActiveAccounts = accounts.length > 0;

  const handlePresetChange = (preset: DateRangePreset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      const days = preset === '7d' ? 7 : preset === '14d' ? 14 : preset === '30d' ? 30 : 90;
      setDateRange(subDays(new Date(), days), new Date());
    }
  };

  const handlePlatformToggle = (platform: SocialPlatform) => {
    if (selectedPlatforms.includes(platform)) {
      setSelectedPlatforms(selectedPlatforms.filter(p => p !== platform));
    } else {
      setSelectedPlatforms([...selectedPlatforms, platform]);
    }
  };

  const getPlatformIcon = (platform: SocialPlatform) => {
    switch (platform) {
      case 'instagram': return Instagram;
      case 'facebook': return Facebook;
      case 'linkedin': return Linkedin;
      case 'tiktok': return Video;
    }
  };

  if (!hasActiveAccounts) {
    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Social Analytics</h2>
            <p className="text-muted-foreground">
              Track your social media performance
            </p>
          </div>
        </div>

        <Card className="border-dashed">
          <CardContent className="py-12 text-center">
            <BarChart3 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Connect an Account First</h3>
            <p className="text-muted-foreground max-w-md mx-auto mb-4">
              Connect a social media account to start tracking your analytics.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Social Analytics</h2>
          <p className="text-muted-foreground">
            Track your social media performance across platforms
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={refresh} disabled={isLoading}>
            <RefreshCw className={cn("h-4 w-4 mr-2", isLoading && "animate-spin")} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap items-center gap-4">
            {/* Date Range */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Period:</span>
              <div className="flex items-center border rounded-lg p-1">
                {(['7d', '14d', '30d', '90d'] as DateRangePreset[]).map((preset) => (
                  <Button
                    key={preset}
                    variant={datePreset === preset ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => handlePresetChange(preset)}
                  >
                    {preset}
                  </Button>
                ))}
                <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant={datePreset === 'custom' ? 'secondary' : 'ghost'}
                      size="sm"
                      className="h-7"
                    >
                      <CalendarIcon className="h-3 w-3 mr-1" />
                      Custom
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="end">
                    <Calendar
                      mode="range"
                      selected={{ from: dateRange.from, to: dateRange.to }}
                      onSelect={(range) => {
                        if (range?.from && range?.to) {
                          setDateRange(range.from, range.to);
                          setDatePreset('custom');
                          setIsCalendarOpen(false);
                        }
                      }}
                      numberOfMonths={2}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <span className="text-sm text-muted-foreground">
                {format(dateRange.from, 'MMM d')} - {format(dateRange.to, 'MMM d, yyyy')}
              </span>
            </div>

            {/* Platform Filter */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Platforms:</span>
              <div className="flex gap-1">
                {accounts.map((account) => {
                  const Icon = getPlatformIcon(account.platform as SocialPlatform);
                  const info = PLATFORM_INFO[account.platform as SocialPlatform];
                  const isSelected = selectedPlatforms.length === 0 ||
                    selectedPlatforms.includes(account.platform as SocialPlatform);

                  return (
                    <Button
                      key={account.id}
                      variant="ghost"
                      size="sm"
                      className={cn(
                        "h-8 px-2",
                        !isSelected && "opacity-40"
                      )}
                      onClick={() => handlePlatformToggle(account.platform as SocialPlatform)}
                    >
                      <Icon className="h-4 w-4" style={{ color: info.color }} />
                    </Button>
                  );
                })}
              </div>
              {selectedPlatforms.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setSelectedPlatforms([])}
                >
                  Clear
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            title="Total Followers"
            value={formatNumber(summary.total_followers)}
            change={summary.followers_change}
            changePercent={summary.followers_change_percent}
            icon={Users}
          />
          <StatCard
            title="Total Engagement"
            value={formatNumber(summary.total_engagement)}
            change={summary.engagement_change}
            changePercent={summary.engagement_change_percent}
            icon={Heart}
          />
          <StatCard
            title="Posts Published"
            value={summary.total_posts.toString()}
            icon={Share2}
          />
          <StatCard
            title="Avg Engagement Rate"
            value={`${summary.avg_engagement_rate}%`}
            icon={TrendingUp}
          />
        </div>
      )}

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <FollowerGrowthChart data={timeSeries} isLoading={isLoading} />
        <EngagementChart data={timeSeries} isLoading={isLoading} />
      </div>

      {/* Platform Comparison */}
      {accounts.length > 1 && (
        <PlatformComparison
          businessId={businessId}
          accounts={accounts}
          dateRange={dateRange}
        />
      )}

      {/* Top Posts */}
      <PostPerformanceTable posts={topPosts} isLoading={isLoading} />
    </div>
  );
};

// Stat Card Component
interface StatCardProps {
  title: string;
  value: string;
  change?: number;
  changePercent?: number;
  icon: React.ElementType;
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  changePercent,
  icon: Icon
}) => {
  const isPositive = (change || 0) >= 0;

  return (
    <Card>
      <CardContent className="pt-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
            {change !== undefined && (
              <div className={cn(
                "flex items-center text-xs mt-1",
                isPositive ? "text-green-600" : "text-red-600"
              )}>
                {isPositive ? (
                  <TrendingUp className="h-3 w-3 mr-1" />
                ) : (
                  <TrendingDown className="h-3 w-3 mr-1" />
                )}
                <span>
                  {isPositive ? '+' : ''}{formatNumber(change)}
                  {changePercent !== undefined && ` (${changePercent}%)`}
                </span>
              </div>
            )}
          </div>
          <div className="rounded-full bg-primary/10 p-3">
            <Icon className="h-5 w-5 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Helper function to format numbers
function formatNumber(num: number): string {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K';
  }
  return num.toLocaleString();
}

export default SocialAnalyticsDashboard;
